import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const CartCtx = createContext(null);
const STORAGE_KEY = "md_cart_v1";

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((product, opts = {}) => {
    const { length = null, colour = null, colourLabel = null, quantity = 1 } = opts;
    setItems((cur) => {
      const idx = cur.findIndex(
        (i) => i.slug === product.slug && i.length === length && i.colour === colour
      );
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], quantity: Math.min(20, next[idx].quantity + quantity) };
        return next;
      }
      return [
        ...cur,
        {
          slug: product.slug,
          name: product.name,
          image: product.image,
          price: product.price,
          digital: product.digital,
          length,
          colour,
          colourLabel,
          quantity,
        },
      ];
    });
    setDrawerOpen(true);
  }, []);

  const remove = useCallback((slug, length, colour) => {
    setItems((cur) =>
      cur.filter((i) => !(i.slug === slug && i.length === length && i.colour === colour))
    );
  }, []);

  const setQty = useCallback((slug, length, colour, qty) => {
    setItems((cur) =>
      cur.map((i) =>
        i.slug === slug && i.length === length && i.colour === colour
          ? { ...i, quantity: Math.max(1, Math.min(20, qty)) }
          : i
      )
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const derived = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const hasPhysical = items.some((i) => !i.digital);
    const shipping = hasPhysical && subtotal < 50 ? 4.95 : 0;
    const total = subtotal + shipping;
    return { subtotal, count, shipping, total, hasPhysical };
  }, [items]);

  return (
    <CartCtx.Provider
      value={{ items, add, remove, setQty, clear, drawerOpen, setDrawerOpen, ...derived }}
    >
      {children}
    </CartCtx.Provider>
  );
};

export const useCart = () => useContext(CartCtx);
